
import { defineComponent, inject, nextTick, onMounted, ref } from "vue";
import { AjaxList, AjaxListFilterData, AjaxListItem, AjaxListProps, makeAjaxListProps } from "@digitalwerk/frontend-muster/vue/composables/ajaxList";
import AjaxForm from "../AjaxForm.vue";
import axios, { AxiosError, AxiosResponse } from "axios";
import VueScrollTo from "vue-scrollto";
import { decode } from "html-entities";

interface ContactListProps extends AjaxListProps {
    listColClass: string,
    formColClass: string,
    itemColClass: string,
    headquarterUid: number,
    manualSelection: boolean,
    boxTitle: string,
    noResultsLabel: string
}

interface ContactListFilterData extends AjaxListFilterData {
    locations: string | '',
    topicSelectbox: '',
    topicCheckbox: [],
    headquarterUid: number,
    manualSelection: boolean
}

class ContactListItem extends AjaxListItem {
    locations: string = '';
    topicsUids: string = '';
}

class ContactList extends AjaxList<ContactListItem, ContactListFilterData> {
    filterItems() {
        this.filteredItems.value = [];

        if (this.filter.data.manualSelection) {
            this.filteredItems.value = this.items.value;
        } else {
            let filteredItems = this.items.value;
            if (this.filter.data.locations) {
                let locationMatch = false, directContacts = [];

                this.filter.data.locations.trim().split(",").forEach((selectedLocation: string) => {
                    if (!locationMatch) {
                        directContacts = this.items.value.filter((contact) => {
                            return contact.locations.trim().split(",").some((contactLocation) => selectedLocation === contactLocation)
                        })
                        filteredItems = directContacts
                        if (directContacts.length > 0) {
                            locationMatch = true;
                        }
                    }
                })
            }
            this.filteredItems.value = filteredItems.filter((contact) => {
                if (this.filter.data.topicSelectbox) {
                    if (!contact.topicsUids?.trim().split(",").some((topic: string) => topic === this.filter.data.topicSelectbox)) {
                        return false
                    }
                }

                if (this.filter.data.topicCheckbox.length > 0) {
                    if (!contact.topicsUids?.trim().split(",").some((topic: string) => this.filter.data.topicCheckbox.some((item) => item === topic))) {
                        return false
                    }
                }
                return !(!this.filter.data.locations && this.filter.data.topicCheckbox.length === 0 && !this.filter.data.topicSelectbox);
            });

            if (this.filteredItems.value.length == 0) {
                this.filteredItems.value = this.items.value.filter((contact) => {
                    return contact.uid === this.filter.data.headquarterUid
                })
            }
        }
    }
}

export default defineComponent({
    name: 'contact-list',
    props: {
        ...makeAjaxListProps(),
        listColClass: String,
        formColClass: String,
        itemColClass: String,
        headquarterUid: {
            type: Number,
            required: true
        },
        onSuccess: {
            type: Function,
            default: null,
        },
        resetFormOnSuccess: {
            type: Boolean,
            default: false
        },
        action: {
            type: String,
            required: true,
        },
        boxTitle: String,
        noResultsLabel: String,
        manualSelection: Boolean
    },
    extends: {AjaxForm},
    setup(props) {
        const action = ref(props.action)
        const form = ref<HTMLFormElement | null>(null);
        const scrollOffset = inject('scrollOffset');

        let htmlResponse = ref("");
        let isLoading = ref<Boolean>(false);
        let properties = {} as { [key: string]: any };

        onMounted(() => {
            form.value?.addEventListener("submit", (e) => {
                e.preventDefault();
                send();
            });

            if (window.location.hash) {
                const element = form.value?.querySelector(window.location.hash);

                if (element) {
                    element.scrollIntoView();
                }
            }
        })

        const getErrorMessage = (propertiesKeys: string) => {
            let messages: Array<string> = [];
            for (let property of propertiesKeys.split(",")) {
                property = property.trim();
                if (properties[property]) {
                    messages = messages.concat(properties[property].map((error: { message: string }) => error.message));
                }
            }
            return messages.join("<br>");
        };

        const handleErrorResponse = async (error: AxiosError) => {
            if (error.response) {
                properties = error.response.data;
                await nextTick();
                let firstErrorElement = form.value?.querySelector(".form-floating .is-invalid");
                if (firstErrorElement) {
                    VueScrollTo.scrollTo(firstErrorElement, {
                        offset: scrollOffset
                    });
                }
            } else {
                console.error("Oops an error occurred.");
            }
        };

        const handleSuccessResponse = (response: AxiosResponse) => {
            if (response.data && response.data.content) {
                htmlResponse.value = decode(response.data.content);
                nextTick(() => {
                    let firstSuccessElement = form.value?.querySelector(".contact-form-success");
                    if (firstSuccessElement) {
                        VueScrollTo.scrollTo(firstSuccessElement, {
                            offset: scrollOffset
                        });
                    }
                });
            }
            if (props.onSuccess) {
                props.onSuccess();
            }
            reset(props.resetFormOnSuccess);
        };

        const hasPropertyError = (propertiesKeys: string) => {
            for (let property of propertiesKeys.split(",")) {
                property = property.trim();
                if (properties[property] && properties[property].length) {
                    return true;
                }
            }
            return false;
        }

        const reset = (resetForm: boolean) => {
            properties = [];
            if (resetForm) {
                form.value?.reset();
            }
        };

        const locationChange = (e: Event) => {
            const selectbox = <HTMLSelectElement>e.target;
            if (selectbox.selectedOptions[0].value || selectbox.selectedOptions[0].dataset.parents) {
                list.filter.data.locations = `${selectbox.selectedOptions[0].value},${selectbox.selectedOptions[0].dataset.parents}`;
            } else {
                list.filter.data.locations = ''
            }
        }

        const send = () => {
            if (form.value) {
                let formData = new FormData(form.value);
                isLoading.value = true;
                axios.post(action.value, formData)
                    .then((response) => {
                        handleSuccessResponse(response);
                    })
                    .catch((error) => {
                        handleErrorResponse(error);
                    })
                    .then(() => {
                        isLoading.value = false;
                    });
            }
        };

        const boxTitle = ref(props.boxTitle);
        const noResultsLabel = ref(props.noResultsLabel);
        const formColClass = ref(props.formColClass);
        const list = new ContactList(props as ContactListProps, {
            locations: '',
            topicSelectbox: '',
            topicCheckbox: [],
            headquarterUid: props.headquarterUid,
            manualSelection: props.manualSelection,
        });

        return {
            action, form, htmlResponse, isLoading, send, hasPropertyError, getErrorMessage,
            list, boxTitle, noResultsLabel, formColClass, locationChange
        }
    }
});
